import React, { useState, useEffect } from 'react';
import { Layout, Button, Breadcrumb, Badge, Card, Spin, Typography, Icon, Divider, Tag, Select, message } from 'antd';
import axios from 'axios';
import { config, general } from '../../../../../constants'
const { Header, Content, Sider } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SOPModal = ({ docEditorFile, data = {}, userId, userName, orgId, shlToken, setEditor }) => {

  // UNIFIED DOCUMENT SOURCE LOGIC
  // IF docEditorFile EXISTS -> USE IT (EDIT MODE)
  // OTHERWISE -> USE data.data (CREATE MODE)
  console.log("docEditorFile", docEditorFile)
  const doc = docEditorFile || (data && data.data) || {};

  const [showMap, setShowMap] = useState(false);


  // State for selected documents in each category
  const [selectedBPSpecific, setSelectedBPSpecific] = useState([]);
  const [selectedSOW, setSelectedSOW] = useState([]);
  const [selectedAppendix, setSelectedAppendix] = useState([]);
  const [selectedGlobalSOPParent, setSelectedGlobalSOPParent] = useState([]);

  // State for available options
  const [availableAppendix, setAvailableAppendix] = useState([]);
  const [availableReference, setAvailableReference] = useState([]);
  const [availableSOW, setAvailableSOW] = useState([]);
  const [availableParents, setAvailableParents] = useState([]);

  // State for ALL items (selected + available)
  const [allBPItems, setAllBPItems] = useState([]);
  const [allSOWItems, setAllSOWItems] = useState([]);
  const [allAppendixItems, setAllAppendixItems] = useState([]);
  const [allGlobalSOPParentItems, setAllGlobalSOPParentItems] = useState([]);

  // State for document data
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for document type data
  const [documentTypeData, setDocumentTypeData] = useState(null);
  const [documentTypeLoading, setDocumentTypeLoading] = useState(false);

  // State for expanded panels
  const [isBPExpanded, setIsBPExpanded] = useState(true);
  const [isSOWExpanded, setIsSOWExpanded] = useState(true);
  const [isAppendixExpanded, setIsAppendixExpanded] = useState(true);
  const [isGlobalSOPParentExpanded, setIsGlobalSOPParentExpanded] = useState(true);

  const [selectedWorkflow, setSelectedWorkflow] = useState([]);
  const [availableWorkflow, setAvailableWorkflow] = useState([]);
  const [allWorkflowItems, setAllWorkflowItems] = useState([]);
  const [isWorkflowExpanded, setIsWorkflowExpanded] = useState(true);

  // Fetch document type if docEditorFile exists and has an ID
  useEffect(() => {
    const fetchDocumentType = async () => {
      if (docEditorFile && docEditorFile.id) {
        setDocumentTypeLoading(true);
        try {
          const response = await axios.get(
            `${config.aiContentGenerationUrl}sop-management/media/${docEditorFile.id}/document-type?org_id=${orgId}`,
            {
              headers: {
                'Authorization': `Bearer ${shlToken}`
              }
            }
          );
          setDocumentTypeData(response.data);
          console.log("Document type data:", response.data);
        } catch (error) {
          console.error("Error fetching document type:", error);
          message.error("Failed to fetch document type");
        } finally {
          setDocumentTypeLoading(false);
        }
      }
    };

    fetchDocumentType();
  }, [docEditorFile, orgId, shlToken]);

  // Fetch relationships data
  useEffect(() => {
    // Only run when doc is available AND orgId is available
    if (!doc || !doc.id || !orgId) {
      setLoading(false);
      return;
    }

    console.log("check doc useeffect", doc)

    const fetchRelationships = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${config.aiContentGenerationUrl}sop-management/media/${doc.id}/relationships?org_id=${orgId}`,
          {
            headers: {
              Authorization: 'Bearer ' + shlToken
            }
          }
        );

        const responseData = response.data;

        console.log("responseData: ", responseData)

        // Set document data
        if (responseData && responseData.document) {
          setDocumentData(responseData.document);
        }

        // Preselected relationships
        if (responseData && responseData.relationships) {
          // BP-Specific SOP - Key Stroke
          if (Array.isArray(responseData.relationships.referenced_by)) {
            const bpIds = responseData.relationships.referenced_by.map(function (item) { return item.id; });
            setSelectedBPSpecific(bpIds);
            // Store the full items for display
            setAllBPItems(responseData.relationships.referenced_by);
          }

          // Global SOW
          if (Array.isArray(responseData.relationships.linked_sows)) {
            const sowIds = responseData.relationships.linked_sows.map(function (item) { return item.id; });
            setSelectedSOW(sowIds);
            // Store the full items for display
            setAllSOWItems(responseData.relationships.linked_sows);
          }

          // Appendix
          if (Array.isArray(responseData.relationships.appendix)) {
            const appendixIds = responseData.relationships.appendix.map(function (item) { return item.id; });
            setSelectedAppendix(appendixIds);
            // Store the full items for display
            setAllAppendixItems(responseData.relationships.appendix);
          }

          // Global SOP Parent (for BP-Specific, SOW, and Appendix docs)
          if (Array.isArray(responseData.relationships.parents)) {
            const parentIds = responseData.relationships.parents.map(function (item) { return item.id; });
            setSelectedGlobalSOPParent(parentIds);
            // Store the full items for display
            setAllGlobalSOPParentItems(responseData.relationships.parents);
          }

          // Workflow
          if (Array.isArray(responseData.relationships.workflows)) {
            const workflowIds = responseData.relationships.workflows.map(function (item) { return item.id; });
            setSelectedWorkflow(workflowIds);
            // Store the full items for display
            setAllWorkflowItems(responseData.relationships.workflows);
          }
        }

        // Available lists - merge with existing items
        if (responseData && responseData.available) {
          if (Array.isArray(responseData.available.appendix)) {
            setAvailableAppendix(responseData.available.appendix);
            // Merge available with already selected
            const existingAppendix = (responseData.relationships && responseData.relationships.appendix) || [];
            const mergedAppendix = mergeUniqueItems(existingAppendix, responseData.available.appendix);
            setAllAppendixItems(mergedAppendix);
          }

          if (Array.isArray(responseData.available.workflow)) {
            setAvailableWorkflow(responseData.available.workflow);
            // Merge available with already selected
            const existingWorkflow = (responseData.relationships && responseData.relationships.workflows) || [];
            const mergedWorkflow = mergeUniqueItems(existingWorkflow, responseData.available.workflow);
            setAllWorkflowItems(mergedWorkflow);
          }

          if (Array.isArray(responseData.available.reference)) {
            setAvailableReference(responseData.available.reference);
            // Merge available with already selected
            const existingBP = (responseData.relationships && responseData.relationships.referenced_by) || [];
            const mergedBP = mergeUniqueItems(existingBP, responseData.available.reference);
            setAllBPItems(mergedBP);
          }
          if (Array.isArray(responseData.available.sow_link)) {
            setAvailableSOW(responseData.available.sow_link);
            // Merge available with already selected
            const existingSOW = (responseData.relationships && responseData.relationships.linked_sows) || [];
            const mergedSOW = mergeUniqueItems(existingSOW, responseData.available.sow_link);
            setAllSOWItems(mergedSOW);
          }
          // Handle parents for Global SOP - Key Stroke
          if (Array.isArray(responseData.available.parents)) {
            setAvailableParents(responseData.available.parents);
            // Check if there are existing parent relationships
            const existingParents = (responseData.relationships && responseData.relationships.parents) || [];
            const mergedParents = mergeUniqueItems(existingParents, responseData.available.parents);
            setAllGlobalSOPParentItems(mergedParents);

            // Set selected parents if they exist
            if (existingParents.length > 0) {
              const parentIds = existingParents.map(function (item) { return item.id; });
              setSelectedGlobalSOPParent(parentIds);
            }
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching relationships:", error);
        message.error("Failed to load relationships data");
        setLoading(false);
      }
    };

    fetchRelationships();
  }, [doc.id, orgId, shlToken]);

  // Helper function to merge arrays without duplicates
  const mergeUniqueItems = (arr1, arr2) => {
    const combined = [...arr1, ...arr2];
    const unique = [];
    const seenIds = {};

    for (let i = 0; i < combined.length; i++) {
      const item = combined[i];
      if (item && item.id && !seenIds[item.id]) {
        seenIds[item.id] = true;
        unique.push(item);
      }
    }

    return unique;
  };

  // Handle Workflow selection change
  const handleWorkflowChange = (values) => {
    const newValues = values.filter(function (v) { return !selectedWorkflow.includes(v); });
    if (newValues.length > 0) {
      const newId = newValues[0];
      handleRelationshipAdd(newId, 'workflow');
    }
    setSelectedWorkflow(values);
  };

  // Get Workflow item by ID
  const getWorkflowItemById = (id) => {
    return allWorkflowItems.find(function (item) { return item.id === id; });
  };

  // Handle POST request when user selects an item
  const handleRelationshipAdd = async (selectedId, relationshipType) => {
    try {
      const targetMediaId = documentData && documentData.id ? documentData.id : (doc && doc.id ? doc.id : null);

      if (!targetMediaId) {
        message.error('Document ID not found');
        return;
      }

      const postData = {
        source_media_id: targetMediaId,
        target_media_id: selectedId,
        relationship_type: relationshipType,
        description: 'add ' + relationshipType
      };


      await axios.post(
        `${config.aiContentGenerationUrl}sop-management/relationships?org_id=${orgId}`,
        postData,
        {
          headers: {
            'Authorization': 'Bearer ' + shlToken,
            'Content-Type': 'application/json'
          }
        }
      );

      message.success('Document Relation added successfully');
    } catch (error) {
      console.error('Error adding relationship:', error);
      message.error('Failed to add relationship');
    }
  };

  // Handle POST request for adding parent
  const handleParentAdd = async (selectedId) => {
    try {
      const targetMediaId = documentData && documentData.id ? documentData.id : (doc && doc.id ? doc.id : null);

      if (!targetMediaId) {
        message.error('Document ID not found');
        return;
      }

      await axios.post(
        `${config.aiContentGenerationUrl}sop-management/media/${targetMediaId}/add-parent?org_id=${orgId}`,
        {
          parent_media_id: selectedId
        },
        {
          headers: {
            'Authorization': 'Bearer ' + shlToken,
            'Content-Type': 'application/json'
          }
        }
      );

      message.success('Global SOP document added successfully');
    } catch (error) {
      console.error('Error adding parent:', error);
      message.error('Failed to add global sop document');
    }
  };

  const getDocTypeColor = (type) => {
    const normalizedType = type && type.trim ? type.trim() : '';
    switch (normalizedType) {
      case 'Global SOP - Key Stroke': return 'blue';
      case 'BP-Specific SOP - Key Stroke': return 'purple';
      case 'Global Statement of Work': return 'orange';
      case 'Appendix Document': return 'green';
      case 'workflow': return 'cyan';
      case 'General': return 'geekblue';
      default: return 'default';
    }
  };

  const getDocTypeIcon = (type) => {
    const normalizedType = type && type.trim ? type.trim() : '';
    switch (normalizedType) {
      case 'Global SOP - Key Stroke': return 'global';
      case 'BP-Specific SOP - Key Stroke': return 'gold';
      case 'Global Statement of Work': return 'file';
      case 'Appendix Document': return 'paper-clip';
      case 'workflow': return 'branches';
      case 'General Document': return 'file-text';
      default: return 'file-text';
    }
  };

  const openInCODEEditor = () => {
    setEditor({
      editorParams: {
        documentName: '',
        documentId: '',
        userId: '',
        userName: ''
      },
      isShowEditor: true
    });
    setShowMap(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleRemoveTag = async (category, id) => {
    try {
      const sourceMediaId = documentData && documentData.id ? documentData.id : (doc && doc.id ? doc.id : null);

      if (!sourceMediaId) {
        message.error('Document ID not found');
        return;
      }

      // Determine if we need to swap the IDs based on document type
      const shouldSwap = shouldShowGlobalSOPParent();

      // Call delete API with conditionally swapped parameters
      await axios.delete(
        `${config.aiContentGenerationUrl}sop-management/remove-relationships`,
        {
          params: {
            source_media_id: shouldSwap ? id : sourceMediaId,
            target_media_id: shouldSwap ? sourceMediaId : id,
            org_id: orgId
          },
          headers: {
            Authorization: 'Bearer ' + shlToken
          }
        }
      );

      // Update state after successful deletion
      switch (category) {
        case 'bpSpecific':
          setSelectedBPSpecific(selectedBPSpecific.filter(function (i) { return i !== id; }));
          break;
        case 'workflow':
          setSelectedWorkflow(selectedWorkflow.filter(function (i) { return i !== id; }));
          break;
        case 'sow':
          setSelectedSOW(selectedSOW.filter(function (i) { return i !== id; }));
          break;
        case 'appendix':
          setSelectedAppendix(selectedAppendix.filter(function (i) { return i !== id; }));
          break;
        case 'globalSOPParent':
          setSelectedGlobalSOPParent(selectedGlobalSOPParent.filter(function (i) { return i !== id; }));
          break;
        default:
          break;
      }

      message.success('Document Relation removed successfully');
    } catch (error) {
      console.error('Error removing relationship:', error);
      message.error('Failed to remove relationship');
    }
  };

  // Handle BP-Specific selection change
  const handleBPSpecificChange = (values) => {
    const newValues = values.filter(function (v) { return !selectedBPSpecific.includes(v); });
    if (newValues.length > 0) {
      const newId = newValues[0];
      handleRelationshipAdd(newId, 'reference');
    }
    setSelectedBPSpecific(values);
  };

  // Handle SOW selection change
  const handleSOWChange = (values) => {
    const newValues = values.filter(function (v) { return !selectedSOW.includes(v); });
    if (newValues.length > 0) {
      const newId = newValues[0];
      handleRelationshipAdd(newId, 'sow_link');
    }
    setSelectedSOW(values);
  };

  // Handle Appendix selection change
  const handleAppendixChange = (values) => {
    const newValues = values.filter(function (v) { return !selectedAppendix.includes(v); });
    if (newValues.length > 0) {
      const newId = newValues[0];
      handleRelationshipAdd(newId, 'appendix');
    }
    setSelectedAppendix(values);
  };

  // Handle Global SOP Parent selection change
  const handleGlobalSOPParentChange = (values) => {
    const newValues = values.filter(function (v) { return !selectedGlobalSOPParent.includes(v); });
    if (newValues.length > 0) {
      const newId = newValues[0];
      handleParentAdd(newId);
    }
    setSelectedGlobalSOPParent(values);
  };

  // Get item by ID from ALL items (not just available)
  const getBPItemById = (id) => {
    return allBPItems.find(function (item) { return item.id === id; });
  };

  const getSOWItemById = (id) => {
    return allSOWItems.find(function (item) { return item.id === id; });
  };

  const getAppendixItemById = (id) => {
    return allAppendixItems.find(function (item) { return item.id === id; });
  };

  const getGlobalSOPParentItemById = (id) => {
    return allGlobalSOPParentItems.find(function (item) { return item.id === id; });
  };

  // Panel component for document categories
  const DocumentPanel = (props) => {
    const title = props.title;
    const icon = props.icon;
    const iconColor = props.iconColor;
    const isExpanded = props.isExpanded;
    const setIsExpanded = props.setIsExpanded;
    const selectedItems = props.selectedItems;
    const handleChange = props.handleChange;
    const availableData = props.availableData;
    const category = props.category;
    const tagColor = props.tagColor;
    const getItemById = props.getItemById;
    const loading = props.loading; // Add this line if loading is not defined

    console.log("selectedItems DocumentPanel", selectedItems)

    return (
      <div style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden' }}>
        <div
          onClick={function () { setIsExpanded(!isExpanded); }}
          style={{
            padding: '12px 16px',
            background: '#fafafa',
            borderBottom: isExpanded ? '1px solid #d9d9d9' : 'none',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'all 0.3s'
          }}
        >
          <span>
            <Icon type={icon} style={{ color: iconColor, marginRight: 8 }} />
            <Text strong style={{ fontSize: 12 }}>{title}</Text>
          </span>
          <span>
            {selectedItems.length > 0 && (
              <Badge count={selectedItems.length} style={{ backgroundColor: iconColor, marginRight: 8 }} />
            )}
            <Icon
              type={isExpanded ? "up" : "down"}
              style={{ fontSize: 12, color: '#8c8c8c' }}
            />
          </span>
        </div>
        {isExpanded && (
          <div style={{
            padding: 16,
            background: '#fff',
            animation: 'slideDown 0.3s ease-out'
          }}>
            <Select
              mode="multiple"
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Select documents"
              value={selectedItems}
              onChange={handleChange}
              allowClear
              maxTagCount={0}
              maxTagPlaceholder={selectedItems.length + ' selected'}
              getPopupContainer={function (trigger) { return trigger.parentNode; }}
              loading={loading}
              showSearch
              optionFilterProp="label"
            >
              {availableData.map(function (item) {
                var displayText = item.title || item.filename; // Add this line
                return (
                  <Option key={item.id} value={item.id} label={displayText}>
                    <span style={{ fontSize: 11 }}>
                      {displayText}
                    </span>
                  </Option>
                );
              })}
            </Select>
            {selectedItems.length > 0 && (
              <div>
                {selectedItems.map(function (id) {
                  const item = getItemById(id);
                  console.log("check item", item)
                  return item ? (
                    <Tag
                      key={id}
                      closable
                      color={tagColor}
                      onClose={function () { handleRemoveTag(category, id); }}
                      style={{ marginBottom: 8, marginRight: 8, fontSize: 11, display: 'inline-block', cursor: 'pointer' }}
                      onClick={function () {
                        setEditor({
                          editorParams: {
                            documentName: item.title,
                            documentId: item.path,
                            userId: userId,
                            userName: userName
                          },
                          isShowEditor: true
                        });
                        setShowMap(false);
                      }}
                    >
                      {item.title || item.filename}
                    </Tag>
                  ) : null;
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  // Check if current document type should show Global SOP parent selector
  const shouldShowGlobalSOPParent = () => {
    const currentDocType = documentData?.document_type_name || doc?.document_type_name;
    return (
      currentDocType === "BP-Specific SOP - Key Stroke" ||
      currentDocType === "Global Statement of Work" ||
      currentDocType === "Appendix Document" ||
      currentDocType === "workflow"
    );
  };

  return (
    <Layout style={{ height: '100vh' }}>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Icon type="home" />
            </Breadcrumb.Item>
            <Breadcrumb.Item>SOP Documents</Breadcrumb.Item>
            <Breadcrumb.Item>{doc.title || 'Document'}</Breadcrumb.Item>
          </Breadcrumb>
        </Header>
        {documentTypeLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 64px)',
            background: '#f5f5f5'
          }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div style={{ background: '#fff', padding: '12px 24px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Tag color={getDocTypeColor(documentData && documentData.document_type_name || doc.document_type_name)}>
                  <Icon type={getDocTypeIcon(documentData && documentData.document_type_name || doc.document_type_name)} /> {(documentData && documentData.document_type_name || doc.document_type_name || 'Unknown Type')
                    .charAt(0).toUpperCase() +
                    (documentData && documentData.document_type_name || doc.document_type_name || 'Unknown Type')
                      .slice(1)
                  }
                </Tag>
                <Text type="secondary">
                  Creator: <strong>{doc.creator_id === userId ? userName : (doc.creator_id || 'N/A')}</strong>
                </Text>
                <Text type="secondary">Created: <strong>{formatDate(doc.created_at)}</strong></Text>
              </div>
            </div>
            <Content style={{ padding: 24, overflow: 'auto', background: '#f5f5f5' }}>
              {showMap ? (
                <Card>
                  <Title level={4}>Document Relationship Map</Title>
                  <div style={{ border: '2px solid #f0f0f0', borderRadius: 8, padding: 40, background: 'linear-gradient(135deg, #fafafa 0%, #e6f7ff 100%)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Card
                        style={{
                          maxWidth: 550,
                          background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                          border: '4px solid #0050b3',
                          textAlign: 'center',
                          marginBottom: 16
                        }}
                      >
                        <div>
                          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>CURRENT DOCUMENT</Text>
                          <div style={{ margin: '8px 0' }}>
                            <Icon type={getDocTypeIcon(documentData && documentData.document_type_name || doc.document_type_name)} style={{ color: '#fff', marginRight: 8 }} />
                            <Text strong style={{ fontSize: 16, color: '#fff' }}>{doc.title || 'Document'}</Text>
                          </div>
                          <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>{documentData && documentData.document_type_name || doc.document_type_name || 'Unknown Type'}</Text>
                        </div>
                      </Card>
                      <div style={{ textAlign: 'center', padding: 40, color: '#8c8c8c' }}>
                        <Icon type="info-circle" style={{ fontSize: 32, marginBottom: 16 }} />
                        <Text type="secondary">No relationship data available</Text>
                      </div>
                    </div>
                  </div>
                  <Divider />
                  <Card size="small" style={{ background: '#fafafa' }}>
                    <Title level={5}>Document Type Legend:</Title>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div><Icon type="global" style={{ color: '#1890ff', marginRight: 8 }} /> <Text>Global SOP - Key Stroke</Text></div>
                      <div><Icon type="gold" style={{ color: '#722ed1', marginRight: 8 }} /> <Text>BP-Specific SOP - Key Stroke</Text></div>
                      <div><Icon type="file" style={{ color: '#fa8c16', marginRight: 8 }} /> <Text>Global Statement of Work</Text></div>
                      <div><Icon type="paper-clip" style={{ color: '#52c41a', marginRight: 8 }} /> <Text>Appendix Document</Text></div>
                      <div><Icon type="branches" style={{ color: '#13c2c2', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>Workflow Document</Text></div>
                    </div>
                  </Card>
                </Card>
              ) : (
                <Card>
                  <div style={{
                    background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 50%, #f9f0ff 100%)',
                    padding: '32px',
                    borderRadius: '12px',
                    border: '1px solid #d9d9d9'
                  }}>
                    <Title level={3} style={{ color: '#262626', marginBottom: '32px', fontWeight: 600 }}>
                      {doc.title || 'Document'}
                    </Title>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '32px'
                    }}>
                      <div style={{
                        flex: 1,
                        //border: '2px dashed #91d5ff',
                        borderRadius: '16px',
                        padding: '40px 32px',
                        textAlign: 'center',
                        background: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div>
                          <Icon type="file-word" style={{ fontSize: 64, color: '#1890ff', marginBottom: 12 }} />
                          <div><Text style={{ color: '#8c8c8c', fontSize: '14px' }}>{doc.title || 'Document'}</Text></div>
                          {doc.file_size && (
                            <div><Text type="secondary" style={{ fontSize: '12px' }}>Size: {doc.file_size} bytes</Text></div>
                          )}
                        </div>
                      </div>
                      <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px'
                      }}>
                        <Button
                          type="primary"
                          size="large"
                          block
                          icon="edit"
                          style={{
                            height: '56px',
                            fontSize: '16px',
                            background: '#1890ff',
                            borderColor: '#1890ff',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(24,144,255,0.3)'
                          }}
                          onClick={openInCODEEditor}
                        >
                          Open In Editor
                        </Button>
                        {/* <Button
                          type="primary"
                          size="large"
                          block
                          icon="edit"
                          style={{
                            height: '56px',
                            fontSize: '16px',
                            background: '#1890ff',
                            borderColor: '#1890ff',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(24,144,255,0.3)'
                          }}
                          onClick={() => { }}
                        >
                          Open Screen Capture Tool
                        </Button> */}
                        {doc.link && (
                          <Button
                            size="large"
                            block
                            icon="download"
                            style={{
                              height: '56px',
                              fontSize: '16px',
                              fontWeight: 500
                            }}
                            onClick={function () { window.open(doc.link, '_blank'); }}
                          >
                            Download File
                          </Button>
                        )}
                      </div>
                    </div>
                    <Divider style={{ margin: '32px 0', borderColor: '#d9d9d9' }} />
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '24px'
                    }}>
                      <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>Document Type</Text>
                          <Text strong style={{ fontSize: '14px', color: '#262626' }}>{documentData && documentData.document_type_name || doc.document_type_name || 'N/A'}</Text>
                        </div>
                      </Card>
                      <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>Creator ID</Text>
                          <Text strong style={{ fontSize: '14px', color: '#262626' }}><strong>{doc.creator_id === userId ? userName : (doc.creator_id || 'N/A')}</strong></Text>
                        </div>
                      </Card>
                      <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>Created Date</Text>
                          <Text strong style={{ fontSize: '14px', color: '#262626' }}>{formatDate(doc.created_at)}</Text>
                        </div>
                      </Card>
                      <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>Last Edited</Text>
                          <Text strong style={{ fontSize: '14px', color: '#262626' }}>{formatDate(doc.updated_at)}</Text>
                        </div>
                      </Card>
                    </div>
                    {doc.description && (
                      <div style={{ marginTop: 24 }}>
                        <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0' }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8 }}>Description</Text>
                          <Text style={{ fontSize: '14px' }}>{doc.description}</Text>
                        </Card>
                      </div>
                    )}
                    <div style={{ marginTop: 24 }}>
                      <Card size="small" style={{ background: '#fff', border: '1px solid #f0f0f0' }}>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 8, textDecoration: 'underline' }}>
                          Additional Information
                        </Text>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>Organization ID: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.organization_id || 'N/A'}</Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>Media Format: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.media_format || 'N/A'}</Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>MIME Type: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.media_mime_type || 'N/A'}</Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>Public: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.is_public ? 'Yes' : 'No'}</Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>Folder: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.is_folder ? 'Yes' : 'No'}</Text>
                          </div>
                          <div>
                            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>Indexed: </Text>
                            <Text style={{ fontSize: '12px' }}>{doc.is_indexed ? 'Yes' : 'No'}</Text>
                          </div>
                        </div>
                      </Card>
                    </div>
                  </div>
                </Card>
              )}
            </Content>
          </>
        )}
      </Layout>
      {!documentTypeLoading && !(documentData?.document_type_name === "General" || doc?.document_type_name === "General") &&
        <Sider width={320} style={{ background: '#fff', borderLeft: '1px solid #f0f0f0', overflow: 'auto' }}>
          <div style={{ padding: 16 }}>
            <Title level={5} style={{ fontSize: 14, marginBottom: 12, color: '#262626' }}>Document Categories</Title>

            {shouldShowGlobalSOPParent() ? (
              <DocumentPanel
                title="Global SOP - Key Stroke"
                icon="global"
                iconColor="#1890ff"
                isExpanded={isGlobalSOPParentExpanded}
                setIsExpanded={setIsGlobalSOPParentExpanded}
                selectedItems={selectedGlobalSOPParent}
                handleChange={handleGlobalSOPParentChange}
                availableData={allGlobalSOPParentItems}
                category="globalSOPParent"
                tagColor="blue"
                getItemById={getGlobalSOPParentItemById}
                handleRemoveTag={handleRemoveTag}
              />
            ) : (
              <>
                <DocumentPanel
                  title="BP-Specific SOP - Key Stroke"
                  icon="gold"
                  iconColor="#722ed1"
                  isExpanded={isBPExpanded}
                  setIsExpanded={setIsBPExpanded}
                  selectedItems={selectedBPSpecific}
                  handleChange={handleBPSpecificChange}
                  availableData={allBPItems}
                  category="bpSpecific"
                  tagColor="purple"
                  getItemById={getBPItemById}
                  handleRemoveTag={handleRemoveTag}
                />
                <DocumentPanel
                  title="Global Statement of Work"
                  icon="file"
                  iconColor="#fa8c16"
                  isExpanded={isSOWExpanded}
                  setIsExpanded={setIsSOWExpanded}
                  selectedItems={selectedSOW}
                  handleChange={handleSOWChange}
                  availableData={allSOWItems}
                  category="sow"
                  tagColor="orange"
                  getItemById={getSOWItemById}
                  handleRemoveTag={handleRemoveTag}
                />
                <DocumentPanel
                  title="Appendix Document"
                  icon="paper-clip"
                  iconColor="#52c41a"
                  isExpanded={isAppendixExpanded}
                  setIsExpanded={setIsAppendixExpanded}
                  selectedItems={selectedAppendix}
                  handleChange={handleAppendixChange}
                  availableData={allAppendixItems}
                  category="appendix"
                  tagColor="green"
                  getItemById={getAppendixItemById}
                  handleRemoveTag={handleRemoveTag}
                />
                <DocumentPanel
                  title="Workflow Document"
                  icon="branches"
                  iconColor="#13c2c2"
                  isExpanded={isWorkflowExpanded}
                  setIsExpanded={setIsWorkflowExpanded}
                  selectedItems={selectedWorkflow}
                  handleChange={handleWorkflowChange}
                  availableData={allWorkflowItems}
                  category="workflow"
                  tagColor="cyan"
                  getItemById={getWorkflowItemById}
                  handleRemoveTag={handleRemoveTag}
                />
              </>
            )}
          </div>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ padding: 16, background: '#fafafa' }}>
            <Title level={5} style={{ fontSize: 12, marginBottom: 12 }}>GSC Document Levels</Title>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Card size="small" bodyStyle={{ padding: 8 }}>
                <div><Icon type="global" style={{ color: '#1890ff', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>Global SOP - Key Stroke</Text></div>
              </Card>
              <Card size="small" bodyStyle={{ padding: 8 }}>
                <div><Icon type="gold" style={{ color: '#722ed1', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>BP-Specific SOP - Key Stroke</Text></div>
              </Card>
              <Card size="small" bodyStyle={{ padding: 8 }}>
                <div><Icon type="file" style={{ color: '#fa8c16', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>Global Statement of Work</Text></div>
              </Card>
              <Card size="small" bodyStyle={{ padding: 8 }}>
                <div><Icon type="paper-clip" style={{ color: '#52c41a', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>Appendix Document</Text></div>
              </Card>
              <Card size="small" bodyStyle={{ padding: 8 }}>
                <div><Icon type="branches" style={{ color: '#13c2c2', marginRight: 8 }} /> <Text style={{ fontSize: 11 }}>Workflow Document</Text></div>
              </Card>
            </div>
          </div>
        </Sider>
      }
    </Layout>
  );
};

export default SOPModal;